import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { MDBTableHead, MDBTable, MDBTableBody } from "mdbreact";
import { Loader } from "../../../UI/Loaders/Loaders";
import { appInsights } from "../../../config/appInsights";
import Paginator from "../../Pagination/Pagination.component";
import CustomSelectDropdown from "../../CustomHTMLElements/CustomSelectDropdown";
import { showModal } from "../../../redux/actions/modal";
import { useGetCorporateCustomers } from "./hooks/useCorporateCustomers";
import CorporateOnboardingModal from "./CorporateOnboarding/CorporateOnboardingModal";

const CorporateOnboarding = () => {
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(false);
  const [resumeCustomerData, setResumeCustomerData] = useState<any>(null);
  const [query, setQuery] = useState({
    pageNumber: 1,
    pageSize: 10,
    startDate: "",
    endDate: "",
    rcNumber: "",
    businessName: "",
    businessType: "",
    status: "",
    onboardingStatus: "",
    searchValue: "",
  });

  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    appInsights.trackPageView({
      name: "BlackList.tsx",
      isLoggedIn: true,
    });
  }, []);

  const {
    data: corporateCustomersResponse,
    isLoading,
    isError: error,
    refetch,
  } = useGetCorporateCustomers({
    params: {
      page: query.pageNumber,
      pageSize: query.pageSize,
      sourceAppId: 5,
      onboardingStartDate: query.startDate,
      onboardingEndDate: query.endDate,
      onboardingStatus: query.onboardingStatus,
      searchValue: query.searchValue,
    },
  });

  const corporateCustomersData = {
    data: corporateCustomersResponse?.data || [],
    totalCount: corporateCustomersResponse?.totalCount || 0,
  };

  let RenderedComponent = null;
  if (!corporateCustomersData && !isLoading) {
    RenderedComponent = (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt">
            <i className="fas fa-bell" /> Unable to Fetch Corporate Onboarding
          </h4>
        </div>
      </div>
    );
  } else if (isLoading) {
    RenderedComponent = (
      <Loader centered={true} text="Loading Corporate Onboarding..." />
    );
  } else {
    if (error) {
      RenderedComponent = (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There was an issue while fetching
              Corporate Onboarding, Please Reload The Page{" "}
            </h4>
          </div>
        </div>
      );
    } else if (
      (corporateCustomersData &&
        corporateCustomersData.data &&
        corporateCustomersData.data.length === 0) ||
      (corporateCustomersData && !corporateCustomersData.data)
    ) {
      RenderedComponent = (
        <div className="centerLoader">
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There are currently no Corporate
              Customers
            </h4>
          </div>
        </div>
      );
    } else {
      RenderedComponent = (
        <>
          <MDBTable
            responsive
            minimal="true"
            striped={false}
            bordered
            hover
            id="table-to-xls"
          >
            <MDBTableHead>
              <tr>
                <th>S/N</th>
                <th>RC Number</th>
                <th>Business Name</th>
                <th>Business Type</th>
                <th>Industry/Sector</th>
                <th>Business Email</th>
                <th>Request Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {corporateCustomersData &&
                corporateCustomersData.data.map((data, _id: number) => {
                  const {
                    biz_email,
                    biz_name,
                    biz_registration_number,
                    business_subtype,
                    modified_date,
                    sector_name,
                    onboarding_status,
                  } = data;

                  return (
                    <tr key={_id}>
                      <td>
                        {" "}
                        {(query?.pageNumber - 1) * query?.pageSize + (_id + 1)}
                      </td>
                      <td>{biz_registration_number || "N/A"}</td>
                      <td>{biz_name || "N/A"}</td>
                      <td>{business_subtype || "N/A"}</td>
                      <td>{sector_name || "N/A"}</td>
                      <td>{biz_email}</td>
                      <td>
                        {modified_date
                          ? moment(modified_date).format("DD-MM-YYYY")
                          : "N/A"}
                      </td>
                      <td>
                        <span>{onboarding_status}</span>
                      </td>
                      <td>
                        <span
                          className="color-mid-blue cursor-pointer font-weight-bold"
                          onClick={() => {
                            if (
                              data.onboarding_status === "LegalInProgress" ||
                              data.onboarding_status === "OnboardingDecline"
                            ) {
                              // Show read-only review modal
                              dispatch(
                                showModal({
                                  modalIdentifier: "viewcorporatecustomer",
                                  dataRecord: data,
                                }),
                              );
                            } else {
                              // Resume incomplete onboarding
                              setResumeCustomerData(data);
                              setIsOnboardingModalOpen(true);
                            }
                          }}
                        >
                          {" "}
                          View Details
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </MDBTableBody>
          </MDBTable>
        </>
      );
    }
  }

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setQuery((prev) => ({
      ...prev,
      pageNumber: 1, // Reset to first page on any filter change
      [name]: name === "pageSize" ? parseInt(value) : value,
    }));
  };

  const handleClear = () => {
    setQuery({
      pageNumber: 1,
      pageSize: 10,
      startDate: "",
      endDate: "",
      rcNumber: "",
      businessName: "",
      businessType: "",
      status: "",
      onboardingStatus: "",
      searchValue: "",
    });
  };

  const handleCreateBusinessCustomer = () => {
    setIsOnboardingModalOpen(true);
  };

  const handleAdvancedSearch = () => {
    dispatch(
      showModal({
        modalIdentifier: "advancedcorporatesearch",
        dataRecord: {
          onSearch: (searchParams: any) => {
            setQuery((prev) => ({
              ...prev,
              pageNumber: 1,
              ...searchParams,
            }));
          },
        },
      }),
    );
  };

  return (
    <div className="datatable-wrapper">
      <div className="table-view speed-1x animated fadeInRight">
        <div className="d-flex justify-content-between mb-4">
          <h3 className="page-title mb-0">Corporate Onboarding</h3>
          <button
            className="btn advancly-btn m-0"
            onClick={handleCreateBusinessCustomer}
          >
            <i className="fas fa-plus" />
            &nbsp; Create New Customer
          </button>
        </div>

        <div className="row">
          <div className="form-group col-md-2">
            <label>From</label>
            <div className="input-group">
              <input
                type="date"
                name="startDate"
                className="form-control"
                value={query.startDate}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="form-group col-md-2">
            <label>To</label>
            <div className="input-group">
              <input
                type="date"
                className="form-control"
                name="endDate"
                value={query.endDate}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="form-group col-md-2" style={{ marginTop: "30px" }}>
            <div className="input-group">
              <CustomSelectDropdown
                name="pageSize"
                value={query.pageSize}
                onChange={handleFilterChange}
                isShowDropdownArrow
              >
                <option value={10}>10 Entries</option>
                <option value={20}>20 Entries</option>
                <option value={50}>50 Entries</option>
                <option value={100}>100 Entries</option>
              </CustomSelectDropdown>
            </div>
          </div>
          <div className="form-group col-md-2" style={{ marginTop: "30px" }}>
            <div className="input-group">
              <button
                className="btn advancly-white-btn form-control"
                onClick={handleAdvancedSearch}
              >
                <i className="fas fa-search-plus" />
                &nbsp; Advanced Search
              </button>
            </div>
          </div>
          <div className="form-group col-md-2" style={{ marginTop: "30px" }}>
            <div className="input-group">
              <button
                type="submit"
                className={`btn advancly-red-btn btn-md ml-3`}
                style={{ height: "3rem" }}
                onClick={handleClear}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div>{RenderedComponent}</div>

        <Paginator
          size={query.pageSize}
          page={query.pageNumber}
          count={corporateCustomersData?.totalCount}
          changeCurrentPage={(pageNumber: number) =>
            setQuery({ ...query, pageNumber: pageNumber })
          }
        />
      </div>

      <CorporateOnboardingModal
        isOpen={isOnboardingModalOpen}
        onClose={() => {
          setIsOnboardingModalOpen(false);
          setResumeCustomerData(null);
          refetch();
        }}
        currentStep="business-type"
        resumeCustomerData={resumeCustomerData}
      />
    </div>
  );
};

export default CorporateOnboarding;
